import React from "react";

import { EDITED_EMPLOYEE } from "EmployerApplication/Actions";
import { companyApplicationApi } from "EmployerApplication/api";
import { getCompanyApplication } from "EmployerApplication/Selectors";
import { api } from "EmployerApplication/urls";
import { getCompanyApplicationByCompanyProfileId } from "EntitiesApi/api";
import createDecorator from "final-form-focus";
import { Form } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import AnalyticsTracking from "Utils/AnalyticsTracking";
import companyToken from "Utils/companyToken";
import fetchWithCSRFToken from "Utils/fetchWithCSRFToken";
import { defaultHandler } from "Utils/http";

import { TitleSmall } from "Cobalt/Header";
import { Modal, ModalBody, ModalHeader, ModalFooter } from "Cobalt/Modal";
import Button from "Common/Button";
import SubmissionErrorWrapper from "Common/forms/SubmissionErrorWrapper";
import Icon from "Common/Icon";

import AddEmployeeForm from "./AddEmployeeForm";

const focusOnErrors = createDecorator();

const CancelButton = ({ onClick, disabled }) => (
  <Button color="gray" onClick={onClick} mr={3} disabled={disabled}>
    Cancel
  </Button>
);

const SubmitButton = ({ disabled, children }) => (
  <Button id="addButton" type="submit" disabled={disabled} aria-label="Confirm add employee">
    {children}
  </Button>
);

const EditEmployeeModal = ({ employee, show, onHide }) => {
  const dispatch = useDispatch();
  const { companyProfileId } = useSelector(getCompanyApplication) || {};
  const onSubmit = async (values) => {
    AnalyticsTracking.trackEvent("cta_clicked", {
      location: "edit_employee_modal",
      label: "update",
    });

    try {
      const response = await fetchWithCSRFToken(api.updateEmployee, {
        method: "PUT",
        body: JSON.stringify({
          employee_hash_id: employee.hash_id,
          employee: values.employee,
          company_id_token: companyToken(),
        }),

        processData: false,
      });

      await defaultHandler(response);
      const json = await response.json();
      dispatch({ type: EDITED_EMPLOYEE, employee: json });
      dispatch(
        getCompanyApplicationByCompanyProfileId(companyProfileId, {
          replace: true,
        }),
      );

      // TODO: Editing an employee should invalidate the 'Application' tag. So we can remove this as well.
      dispatch(companyApplicationApi.endpoints.getCurrentCompanyApplication.initiate());

      onHide();
    } catch (e) {
      new SubmissionErrorWrapper(e)
        .forForbiddenError(() => {
          window.location.href = "/unexpected-error";
        })
        .orRethrow();
    }
  };
  const handleClose = () => {
    AnalyticsTracking.trackEvent("cta_clicked", {
      location: "edit_employee_modal",
      label: "cancel",
    });

    onHide();
  };

  const employeeFullName = employee ? employee.full_name.replace(/^\s+|\s+$/g, "") : "this employee";

  const initialValues = {
    employee: {
      ...employee,
      employee_fill: employee.email ? "Yes" : "No",
    },
  };

  return (
    <Modal show={show} onHide={handleClose} className="EditEmployeeModal">
      <Form onSubmit={onSubmit} decorators={[focusOnErrors]} initialValues={initialValues}>
        {({ handleSubmit, submitting, invalid, values }) => {
          const { employee: { employee_fill } = {} } = values;
          return (
            <form onSubmit={handleSubmit}>
              <ModalHeader closeButton>
                <TitleSmall>Edit Employee</TitleSmall>
              </ModalHeader>
              <ModalBody>
                <AddEmployeeForm employeeFullName={employeeFullName} employeeWillFillApplication={employee_fill} canLinkAccount={false} />
              </ModalBody>
              <ModalFooter>
                <CancelButton onClick={handleClose} disabled={submitting} />
                <SubmitButton disabled={invalid || submitting}>
                  {submitting ? (
                    <>
                      <Icon icon="cog" spin mr={3} flavor="solid" />
                      Loading...
                    </>
                  ) : (
                    "Update"
                  )}
                </SubmitButton>
              </ModalFooter>
            </form>
          );
        }}
      </Form>
    </Modal>
  );
};

export default EditEmployeeModal;
